/**
 * The reviewer's whole experience (Phase 3): Home (browse by valuer) -> a valuer's reports
 * -> the report workspace. A tiny hash router (reviewer/router.ts) gives every screen —
 * and every finding's page jump — a shareable URL, consistent with the rest of web/'s
 * "no route library" approach.
 */
import { useMemo } from 'react';
import { useReviewerData } from '../../reviewer/useReviewerData';
import { useHashRoute } from '../../reviewer/router';
import { Banner, Button, Spinner, Tile } from '../../components/ui';
import { ReviewerHome } from './ReviewerHome';
import { ValuerReports } from './ValuerReports';
import { ReportWorkspace } from './ReportWorkspace';
import { AdminApp } from '../admin/AdminApp';

export function ReviewerApp({ isAdmin }: { isAdmin: boolean }) {
  const { state, reload } = useReviewerData();
  const [route, navigate] = useHashRoute();

  const reportForRoute = useMemo(() => {
    if (state.status !== 'ready' || route.name !== 'report') return null;
    return state.reports.find((r) => r.id === route.reportId) ?? null;
  }, [state, route]);

  if (state.status === 'loading') {
    return (
      <main className="main">
        <Tile>
          <Spinner label="Loading the dashboard…" />
        </Tile>
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="main">
        <div className="stack">
          <Banner tone="error">{state.message}</Banner>
          <div>
            <Button variant="secondary" onClick={() => void reload()}>
              Try again
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (route.name === 'admin') {
    if (!isAdmin) {
      return (
        <main className="main">
          <Tile className="tile--pad-lg">
            <p className="muted">Your account does not have admin access.</p>
          </Tile>
        </main>
      );
    }
    return <AdminApp section={route.section} onNavigateSection={(section) => navigate({ name: 'admin', section })} />;
  }

  if (route.name === 'valuer') {
    const valuer = state.valuers.find((v) => v.id === route.valuerId) ?? null;
    return (
      <ValuerReports
        valuer={valuer}
        reports={state.reports.filter((r) => r.valuerId === route.valuerId)}
        checkResults={state.checkResults}
        riskResults={state.riskResults}
        errorFindings={state.errorFindings}
        onBack={() => navigate({ name: 'home' })}
        onOpenReport={(reportId) => navigate({ name: 'report', reportId })}
      />
    );
  }

  if (route.name === 'report') {
    return (
      <ReportWorkspace
        report={reportForRoute}
        valuers={state.valuers}
        banks={state.banks}
        checkSchema={state.checkSchema}
        riskSchema={state.riskSchema}
        checkResults={state.checkResults.filter((c) => c.reportId === route.reportId)}
        riskResults={state.riskResults.filter((r) => r.reportId === route.reportId)}
        errorFindings={state.errorFindings.filter((e) => e.reportId === route.reportId)}
        initialDoc={route.doc}
        initialPage={route.page}
        isAdmin={isAdmin}
        onBack={() => {
          const valuerId = reportForRoute?.valuerId;
          navigate(valuerId ? { name: 'valuer', valuerId } : { name: 'home' });
        }}
        onOpenRevision={(reportId) => navigate({ name: 'report', reportId })}
      />
    );
  }

  return (
    <ReviewerHome
      valuers={state.valuers}
      banks={state.banks}
      reports={state.reports}
      checkResults={state.checkResults}
      riskResults={state.riskResults}
      errorFindings={state.errorFindings}
      onOpenValuer={(valuerId) => navigate({ name: 'valuer', valuerId })}
      onOpenReport={(reportId) => navigate({ name: 'report', reportId })}
    />
  );
}
